import React, { useState, forwardRef } from 'react';
import styled from 'styled-components';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const PasswordInputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInputField = styled.input`
  width: 100%;
  padding: ${props => props.padding || '0.75rem'};
  padding-right: ${props => props.paddingRight || '2.5rem'};
  border: ${props => props.border || '2px solid var(--border-color)'};
  border-radius: ${props => props.borderRadius || 'var(--radius-md)'};
  font-size: ${props => props.fontSize || '1rem'};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.focusBorderColor || 'var(--primary-blue)'};
    box-shadow: ${props => props.focusBoxShadow || '0 0 0 3px rgb(0 78 158 / 0.1)'};
  }
  
  &.error {
    border-color: ${props => props.errorBorderColor || 'var(--error)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: ${props => props.buttonRight || '0.75rem'};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor || '#6b7280'};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.iconHoverColor || '#374151'};
  }
  
  &:focus {
    outline: none;
    color: ${props => props.iconFocusColor || 'var(--primary-blue)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    font-size: ${props => props.iconSize || '1.25rem'};
  }
`;

// Use forwardRef for Controller compatibility
const PasswordInput = forwardRef(({
  id,
  value,
  onChange: controlledOnChange,
  name: nameProp, // Also capture name as direct prop (from register spread)
  placeholder,
  className,
  disabled,
  required,
  minLength,
  maxLength,
  autoComplete,
  // Styling props
  padding,
  paddingRight,
  border,
  borderRadius,
  fontSize,
  focusBorderColor,
  focusBoxShadow,
  errorBorderColor,
  buttonRight,
  iconColor,
  iconHoverColor,
  iconFocusColor,
  iconSize,
  // React Hook Form Controller field (if used) - MUST be last to capture all field props
  ...fieldProps
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  // Debug logging
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 PasswordInput Debug:', {
        hasFieldProps: fieldProps && Object.keys(fieldProps).length > 0,
        fieldPropsKeys: fieldProps ? Object.keys(fieldProps) : [],
        hasName: fieldProps?.name,
        hasRef: !!fieldProps?.ref,
        hasOnChange: !!fieldProps?.onChange,
        hasValue: value !== undefined || fieldProps?.value !== undefined,
        hasOnChangeProp: !!controlledOnChange,
        showPassword
      });
    }
  }, [fieldProps, value, controlledOnChange, showPassword]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle both controlled (value/onChange) and uncontrolled (react-hook-form) inputs
  // Build base props (excluding type, which will be set dynamically)
  const baseProps = {
    id,
    placeholder,
    className,
    disabled,
    required,
    minLength,
    maxLength,
    autoComplete: autoComplete || 'current-password',
    padding,
    paddingRight,
    border,
    borderRadius,
    fontSize,
    focusBorderColor,
    focusBoxShadow,
    errorBorderColor
  };

  // When using {...register()} from react-hook-form, it spreads { name, onChange, onBlur, ref }
  // These get destructured: onChange -> controlledOnChange, name -> nameProp, rest -> fieldProps
  // Check if we have field props by looking for:
  // - nameProp (direct prop from register spread)
  // - fieldProps.name (from Controller)
  // - controlledOnChange (onChange from register spread)
  const hasFieldProps = !!nameProp || (fieldProps && fieldProps.name) || !!controlledOnChange;
  
  if (hasFieldProps) {
    // When using {...register()}, we get:
    // - onChange as controlledOnChange (destructured prop)
    // - name, onBlur, ref in fieldProps (or name might be direct prop)
    // We need to combine the ref from field with our forwarded ref
    const fieldRef = fieldProps?.ref;
    
    // Create a combined ref callback
    const combinedRef = (element) => {
      // Call field's ref (from register or Controller)
      if (fieldRef) {
        if (typeof fieldRef === 'function') {
          fieldRef(element);
        } else if (fieldRef && typeof fieldRef === 'object') {
          fieldRef.current = element;
        }
      }
      // Call forwarded ref
      if (ref) {
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref && typeof ref === 'object') {
          ref.current = element;
        }
      }
    };
    
    // Get field name - could be in fieldProps (from Controller) or as a direct prop (from register spread)
    const fieldName = nameProp || fieldProps?.name;
    
    // Get field value - check fieldProps.value, value prop, or empty string
    const fieldValue = fieldProps?.value !== undefined ? fieldProps.value : (value !== undefined ? value : '');
    
    // CRITICAL: When using {...register()}, onChange is passed as controlledOnChange (destructured prop)
    // When using Controller, onChange is in fieldProps.onChange
    // Use controlledOnChange first (from register spread), then fallback to fieldProps.onChange
    const fieldOnChange = controlledOnChange || fieldProps?.onChange;
    
    // Ensure we have an onChange handler - this is critical for the input to work
    if (!fieldOnChange) {
      console.error('PasswordInput: No onChange handler found!', {
        hasControlledOnChange: !!controlledOnChange,
        hasFieldPropsOnChange: !!fieldProps?.onChange,
        fieldPropsKeys: fieldProps ? Object.keys(fieldProps) : []
      });
    }
    
    // Get onBlur from fieldProps
    const fieldOnBlur = fieldProps?.onBlur;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 PasswordInput fieldProps (from register/Controller):', {
        keys: fieldProps ? Object.keys(fieldProps) : [],
        name: fieldName,
        hasControlledOnChange: !!controlledOnChange,
        hasFieldPropsOnChange: !!fieldProps?.onChange,
        finalOnChange: !!fieldOnChange,
        onBlur: fieldOnBlur,
        ref: fieldRef,
        fieldPropsValue: fieldProps?.value,
        valueProp: value,
        finalValue: fieldValue
      });
    }
    
    // Merge base props with field props
    // CRITICAL: Use fieldOnChange which prioritizes controlledOnChange (from register spread)
    const inputProps = {
      ...baseProps,
      name: fieldName, // Field name from fieldProps
      onChange: fieldOnChange, // Use controlledOnChange (from register) or fieldProps.onChange
      onBlur: fieldOnBlur, // onBlur from fieldProps
      value: fieldValue, // Use fieldProps.value or value prop or empty string
      type: showPassword ? 'text' : 'password', // Dynamic type for password visibility
      ref: combinedRef, // Combined ref
    };
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 PasswordInput render (Controller mode):', {
        name: fieldProps.name,
        hasOnChange: !!fieldProps.onChange,
        hasOnBlur: !!fieldProps.onBlur,
        hasRef: !!fieldProps.ref,
        hasValue: fieldProps.value !== undefined,
        type: inputProps.type,
        showPassword,
        fieldPropsKeys: Object.keys(fieldProps),
        inputPropsHasOnChange: !!inputProps.onChange,
        inputPropsHasOnBlur: !!inputProps.onBlur,
        inputPropsHasName: !!inputProps.name,
        inputPropsValue: inputProps.value
      });
    }
    
    return (
      <PasswordInputContainer>
        <PasswordInputField
          {...inputProps}
        />
        <ToggleButton
          type="button"
          onClick={togglePasswordVisibility}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          buttonRight={buttonRight}
          iconColor={iconColor}
          iconHoverColor={iconHoverColor}
          iconFocusColor={iconFocusColor}
          iconSize={iconSize}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </ToggleButton>
      </PasswordInputContainer>
    );
  } else {
    // Controlled component - use value/onChange
    const controlledProps = { ...baseProps };
    if (value !== undefined) {
      controlledProps.value = value;
    }
    if (controlledOnChange) {
      controlledProps.onChange = controlledOnChange;
    }
    controlledProps.type = showPassword ? 'text' : 'password';
    
    return (
      <PasswordInputContainer>
        <PasswordInputField
          {...controlledProps}
        />
        <ToggleButton
          type="button"
          onClick={togglePasswordVisibility}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          buttonRight={buttonRight}
          iconColor={iconColor}
          iconHoverColor={iconHoverColor}
          iconFocusColor={iconFocusColor}
          iconSize={iconSize}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </ToggleButton>
      </PasswordInputContainer>
    );
  }

});

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;

